import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { advisorsList, AdvisorAdsCard } from "@/features/advisors"

import { getMoonImage, moonDirection } from "../../config/moon-phases"
import { useAstroToday } from "../../hooks/use-astro-today"
import { TodayTimer } from "../today-timer"
import { TodayLoader } from "../today-loader"
import { PersonalizedReports } from "../personalized-reports"
import { ADVISOR_PRESETS } from "../../config/advisor-presets"
import { MoonHero } from "./moon-hero"
import { DailyMessage } from "./daily-message"
import { MoonDiagram } from "./moon-diagram"
import { MoonParameters } from "./moon-parameters"
import { DistanceFromEarth } from "./distance-from-earth"
import { PlanetaryRetrogrades } from "./planetary-retrogrades"

/** Normalise illumination to a 0-100 value (the API may return 0-1 or 0-100). */
const toIllumination = (value: number | null): number => {
  if (value == null) return 0
  return value <= 1 ? value * 100 : value
}

/** Moon phase category — today's lunar guidance from the backend. Ported from Today/MoonPhase. */
export function MoonCategory() {
  const navigate = useNavigate()
  const { setSelectedAdvisor, setPreSelectedChatMessage } = useChat()
  // Approximate the observer location from the browser timezone instead of
  // prompting for geolocation. The backend maps the zone to representative
  // coordinates for the moonrise/moonset times. The day boundary stays UTC.
  const requestContext = useMemo(
    () => ({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" }),
    [],
  )
  const { data, meta, loading } = useAstroToday(["moon_phases"], requestContext)

  const moon = data?.moon_phases

  const askAdvisor = (preset?: string) => {
    if (preset) setPreSelectedChatMessage(preset)
    setSelectedAdvisor(advisorsList[11])
    navigate({ to: WebappRoutes.READINGS })
  }

  if (loading) return <TodayLoader />
  if (!moon || !moon.moon_phase) return <TodayLoader empty />

  const illumination = toIllumination(moon.hero?.phase.illumination_percent ?? moon.illumination)
  // One decimal for the displayed %, a rounded integer for picking the artwork.
  const illuminationLabel = `${illumination.toFixed(1)}%`
  const imageDirection =
    moon.hero?.phase.direction === "waning" || moon.hero?.phase.direction === "waxing"
      ? moon.hero.phase.direction
      : moonDirection(moon.moon_phase)
  const moonImage = getMoonImage(Math.round(illumination), imageDirection)
  const phaseName = moon.hero?.phase.name ?? moon.moon_phase
  const description =
    moon.hero?.summary ||
    moon.report ||
    "Tonight's moon sets the mood for reflection and renewal."

  return (
    <div className="w-full">
      <MoonHero
        moonImage={moonImage}
        phaseName={phaseName}
        description={description}
        extras={{
          dayNumber:
            moon.hero?.lunar_day.number != null
              ? String(moon.hero.lunar_day.number)
              : moon.moon_day != null
                ? String(moon.moon_day)
                : "",
          daySymbolName: moon.hero?.lunar_day.symbol ?? "",
          lunarSignSymbol: moon.hero?.lunar_sign.symbol ?? "",
          lunarSignName: moon.hero?.lunar_sign.name || moon.moon_sign || "",
          phasePercent: illuminationLabel,
          times: {
            // Events can legitimately be null (e.g. the moon doesn't set on a
            // given local day) — show a dash rather than an empty box.
            moonUp: moon.hero?.events.moonrise?.local || "—",
            moonDown: moon.hero?.events.moonset?.local || "—",
            sunUp: moon.hero?.events.sunrise?.local || "—",
            sunDown: moon.hero?.events.sunset?.local || "—",
          },
        }}
      />

      <DailyMessage affirmation={moon.hero?.affirmation} lifestyle={moon.lifestyle} />

      {/* Advisor */}
      <div className="px-4">
        <h3 className="mb-2 mt-10 font-display text-2xl font-medium text-purple-light">
          Need lunar clarity today?
        </h3>
        <p className="mb-6 font-body text-base text-purple-light/70">
          Let a lunar advisor help you navigate today’s energy — tailored insights for your unique
          questions and intentions.
        </p>
        <AdvisorAdsCard
          advisor={advisorsList[11]}
          message={`The ${phaseName} is lighting your path. What would you like to align with its energy today?`}
          presets={[...ADVISOR_PRESETS.moon]}
          onPresetClick={askAdvisor}
        />
      </div>

      <MoonDiagram diagram={moon.diagram} />
      <MoonParameters parameters={moon.parameters} />
      <DistanceFromEarth distance={moon.distance} />
      <PlanetaryRetrogrades retrogrades={moon.retrogrades} />
      <PersonalizedReports />

      {/* Countdown */}
      <div className="mt-10 px-4">
        <div className="h-px w-full bg-purple-light/20" />
        <div className="mt-6 flex min-h-[51px] items-center gap-4">
          <p className="min-w-0 flex-1 font-body text-base text-purple-light/70">
            Come back tomorrow to reveal your new lunar guidance
          </p>
          {meta && <TodayTimer target={meta.next_sync} />}
        </div>
      </div>
    </div>
  )
}
